import {toRef} from 'vue';
import {storeToRefs} from 'pinia';
import {mdl} from '@malt/common';
import {useFeatureFlag} from '@malt/feature-flags';
import {escapeHtml} from '@malt/sanitize';
import {toAppraisalItems} from '~/components/_common/appraisals/AppraisalItem';
import {toRatingItem} from '~/components/_common/appraisals/RatingsItem';
import type {EducationItem} from '~/components/_common/education/EducationItem';
import {type ExperienceItem, toExperienceLogoSrc} from '~/components/_common/experience/ExperienceItem';
import {toLanguageItems} from '~/components/_common/language/LanguageItem';
import {useAppraisalStore} from '~/features/appraisals/store/appraisalStore';
import {useRecommendationsStore} from '~/features/recommendations/store/recommendations.store';
import type {AppraisalAverageRating} from '~/types/domain/Appraisals';
import type {Educations} from '~/types/domain/Educations';
import type {Experiences} from '~/types/domain/Experiences';
import type {AvailabilityItem} from '@/components/_common/availability/AvailabilityItem';
import {AvailabilityItemFrequency, AvailabilityItemStatus} from '@/components/_common/availability/AvailabilityItem';
import {toRecommendations} from '@/components/_common/recommendations/RecommendationItem';
import {useProfileStore} from '@/store/profileStore';
import type {AddressResource, AvailabilityFrequencyResource, AvailabilityResource} from '@/types/api-raw-types';

export const useProfileStoreReadOnly = () => {
    const {appraisals} = storeToRefs(useAppraisalStore());
    const {profile, educations, experiences, industryExpertises} = storeToRefs(useProfileStore());
    const {recommendations} = storeToRefs(useRecommendationsStore());
    const isNewCompanyLogoEnabled = useFeatureFlag('new-company-logo');

    return {
        appraisalsWithComment: toRef(
            toAppraisalItems(appraisals.value?.appraisals?.filter((appraisal) => appraisal.comment && !appraisal.hidden) ?? []),
        ),
        availability: toRef(profile.value?.availability ? _toAvailabilityItem(profile.value.availability) : undefined),
        description: toRef(mdl(escapeHtml(profile.value?.description))),
        educations: toRef(toEducationItems(educations.value!)),
        experiences: toRef(toExperienceItems(experiences.value!, isNewCompanyLogoEnabled.value)),
        industryExpertises: toRef(industryExpertises.value!),
        firstname: toRef(profile.value?.firstname),
        languages: toRef(profile.value?.languages ? toLanguageItems(profile.value.languages) : undefined),
        location: toRef(profile.value?.locationPreferences.address ? toLocation(profile.value.locationPreferences.address) : undefined),
        ratings: toRef(
            toRatingItem(
                appraisals.value?.numberOfAppraisalsWithRating ?? 0,
                appraisals.value?.ratings ?? ({general: 0, communication: 0, delay: 0, quality: 0} as AppraisalAverageRating),
            ),
        ),
        recommendations: toRef(toRecommendations(recommendations.value!)),
        locationPreferences: toRef(profile.value?.locationPreferences),
        price: toRef(profile.value?.price),
    };
};

// Temporary mappers
function _toAvailabilityItemStatus(availabilityResourceStatus: string): AvailabilityItemStatus {
    switch (availabilityResourceStatus) {
        case 'AVAILABLE_AND_VERIFIED':
            return AvailabilityItemStatus.AVAILABLE_AND_VERIFIED;
        case 'AVAILABLE_SOON':
            return AvailabilityItemStatus.AVAILABLE_SOON;
        case 'NOT_AVAILABLE_WITH_DATE':
            return AvailabilityItemStatus.NOT_AVAILABLE_WITH_DATE;
        case 'NOT_AVAILABLE':
            return AvailabilityItemStatus.NOT_AVAILABLE;
        case 'AVAILABLE':
        default:
            return AvailabilityItemStatus.AVAILABLE;
    }
}

function _toAvailabilityItemFrequency(availabilityResourceFrequency: AvailabilityFrequencyResource): AvailabilityItemFrequency {
    switch (availabilityResourceFrequency) {
        case 'ONE_DAY_PER_WEEK':
            return AvailabilityItemFrequency.ONE_DAY_PER_WEEK;
        case 'TWO_DAYS_PER_WEEK':
            return AvailabilityItemFrequency.TWO_DAYS_PER_WEEK;
        case 'THREE_DAYS_PER_WEEK':
            return AvailabilityItemFrequency.THREE_DAYS_PER_WEEK;
        case 'FOUR_DAYS_PER_WEEK':
            return AvailabilityItemFrequency.FOUR_DAYS_PER_WEEK;
        case 'EVENINGS_AND_WEEKENDS':
            return AvailabilityItemFrequency.EVENINGS_AND_WEEKENDS;
        case 'FULL_TIME':
        default:
            return AvailabilityItemFrequency.FULL_TIME;
    }
}

function _toAvailabilityItem(availabilityResource: AvailabilityResource): AvailabilityItem | undefined {
    if (availabilityResource) {
        return {
            confirmed: availabilityResource.details.verified,
            status: _toAvailabilityItemStatus(availabilityResource.details.availability),
            frequency: _toAvailabilityItemFrequency(availabilityResource.details.frequency),
            isPartial: availabilityResource.details.label === 'PARTIAL',
            nextAvailabilityDate: new Date(availabilityResource.details.nextAvailabilityDate),
        };
    }

    return undefined;
}

function toLocation(addressResource: AddressResource): string | undefined {
    if (addressResource) {
        return addressResource?.formattedAddress;
    }
    return undefined;
}

function toEducationItems(educations: Educations): EducationItem[] {
    return educations.items.map((educ) => ({
        description: educ.description,
        title: educ.title,
        graduationYear: educ.graduationYear,
        content: educ.content,
    }));
}

export function toExperienceItems(experiences: Experiences, isNewCompanyLogoEnabled: boolean): ExperienceItem[] {
    return experiences.items.map((xp) => ({
        company: xp.company,
        title: xp.title,
        activityArea: xp.area,
        location: xp.location?.formattedAddress,
        startDate: new Date(xp.startDate),
        endDate: xp.endDate ? new Date(xp.endDate) : undefined,
        currentJob: xp.currentJob,
        description: xp.description,
        skills: xp.skills?.map((skill) => skill.label),
        logoSrc: toExperienceLogoSrc(xp, isNewCompanyLogoEnabled),
        missionId: xp.missionId,
    }));
}
// Temporary mappers: end
